import { useQuery } from "@tanstack/react-query";

import { ISO3TO2 } from "constants/countries";
import { useDebounce } from "hooks/useDebounce";

const SEARCH_DEBOUNCE_MS = 300;

const startsWithNumber = query => /^\d/.test(query);

// Keeps street-level results. Queries that start with a number are already
// limited to addresses, and India is exempt because its addresses rarely
// carry street types. Anything else is kept when it has a type beyond
// geocode and route, which drops bare city and road results.
const isStreetPrediction = (prediction, query, country) => {
  if (startsWithNumber(query)) return true;
  if (ISO3TO2[country] === "IN") return true;
  if (!prediction.types) return false;
  if (prediction.types.includes("street_address")) return true;
  const broadTypes = prediction.types.filter(type =>
    ["geocode", "route"].includes(type)
  );
  return prediction.types.length > broadTypes.length;
};

const toPrediction = prediction => ({
  placeId: prediction.place_id,
  street: prediction.structured_formatting.main_text,
  label: `${prediction.structured_formatting.main_text} ${prediction.structured_formatting.secondary_text}`,
});

// When autocomplete finds nothing, the result stays empty and the typed text
// is kept as the street. The legacy field fell back to a find-place lookup,
// which matches some nearby place even for nonsense text and then filled the
// address from that guess.
const fetchPredictions = (services, query, country) =>
  new Promise(resolve => {
    const request = {
      input: query,
      ...(startsWithNumber(query) && { types: ["address"] }),
      ...(country && {
        componentRestrictions: { country: ISO3TO2[country] },
      }),
    };
    services.autocomplete.getPlacePredictions(
      request,
      (predictions, status) => {
        if (status !== services.statusOk) {
          resolve([]);
          return;
        }
        resolve(
          predictions
            .filter(prediction =>
              isStreetPrediction(prediction, query, country)
            )
            .map(toPrediction)
        );
      }
    );
  });

/** Resolves a prediction to its full `PlaceResult`. */
export const fetchPlaceDetails = (services, placeId) =>
  new Promise((resolve, reject) => {
    services.details.getDetails(
      {
        placeId,
        fields: ["address_component", "formatted_address", "geometry"],
      },
      (place, status) => {
        if (status === services.statusOk) {
          resolve(place);
        } else {
          reject(new Error(`Place details failed: ${status}`));
        }
      }
    );
  });

/**
 * Street predictions for the debounced query, restricted to `country` (ISO
 * alpha-3) when one is set. Disabled until the services load.
 *
 * `isSearching` also covers the debounce wait, so a caller does not report
 * "no matches" for text that has not been searched yet.
 */
export const usePlacePredictions = ({ services, query, country }) => {
  const trimmedQuery = query.trim();
  const debouncedQuery = useDebounce(trimmedQuery, SEARCH_DEBOUNCE_MS);

  const result = useQuery({
    // The service instances do not hash, so the key records only that they
    // are loaded.
    queryKey: [
      "places",
      "predictions",
      debouncedQuery,
      country ?? null,
      Boolean(services),
    ],
    queryFn: () => fetchPredictions(services, debouncedQuery, country),
    enabled: Boolean(services) && debouncedQuery !== "",
    staleTime: Infinity,
  });

  const isSearching =
    Boolean(services) &&
    trimmedQuery !== "" &&
    (trimmedQuery !== debouncedQuery || result.isFetching);

  return { data: result.data, isFetching: result.isFetching, isSearching };
};
